import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import iziToast from 'izitoast';

const CustomCodeTab = () => {
  const [snippets, setSnippets] = useState(() => {
    const loaded = window.thhaAdminSettings.custom_codes;
    return Array.isArray(loaded) ? loaded : [];
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [type, setType] = useState('html');
  const [scope, setScope] = useState('everywhere');
  const [location, setLocation] = useState('head');
  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);

  const handleAddNew = () => {
    setTitle('');
    setType('html');
    setScope('everywhere');
    setLocation('head');
    setCode('');
    setActive(true);
    setEditItem(null);
    setIsEditing(true);
  };

  const handleEdit = (item) => {
    setTitle(item.title || '');
    setType(item.type || 'html');
    setScope(item.scope || 'everywhere');
    setLocation(item.location || 'head');
    setCode(item.code || '');
    setActive(item.active ? true : false);
    setEditItem(item);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditItem(null);
  };

  const saveToDb = (updatedList, successMessage) => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append('action', 'thha_save_custom_codes');
    formData.append('_wpnonce', window.thhaAdminSettings.saveNonce);
    formData.append('custom_codes', JSON.stringify(updatedList));

    fetch(window.thhaAdminSettings.ajaxurl, {
      method: 'POST',
      body: formData,
    })
      .then(res => res.json())
      .then(response => {
        if (response.success) {
          iziToast.show({
            message: successMessage || __('Settings saved successfully!', 'huge-addons-for-elementor'),
            position: 'topRight',
            backgroundColor: '#FFFFFF',
            messageColor: '#19192C',
            progressBarColor: '#0095FF',
          });
          setSnippets(updatedList);
          window.thhaAdminSettings.custom_codes = updatedList;
          setIsEditing(false);
          setEditItem(null);
        } else {
          throw new Error(response.data || __('Failed to save settings.', 'huge-addons-for-elementor'));
        }
      })
      .catch(error => {
        iziToast.error({
          message: error.message,
          position: 'topRight',
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!title.trim()) {
      iziToast.warning({
        message: __('Please enter a snippet title.', 'huge-addons-for-elementor'),
        position: 'topRight',
      });
      return;
    }

    if (!code.trim()) {
      iziToast.warning({
        message: __('Please enter the code.', 'huge-addons-for-elementor'),
        position: 'topRight',
      });
      return;
    }

    let updatedList;
    if (editItem) {
      // Edit mode
      updatedList = snippets.map(s => {
        if (s.id === editItem.id) {
          return {
            ...s,
            title,
            type,
            scope: type === 'php' ? scope : 'everywhere',
            location: type !== 'php' ? location : 'head',
            code,
            active
          };
        }
        return s;
      });
    } else {
      // Add mode
      const newItem = {
        id: 'snippet_' + Date.now(),
        title,
        type,
        scope: type === 'php' ? scope : 'everywhere',
        location: type !== 'php' ? location : 'head',
        code,
        active
      };
      updatedList = [...snippets, newItem];
    }

    saveToDb(updatedList, __('Snippet saved successfully!', 'huge-addons-for-elementor'));
  };

  const handleToggleActive = (item) => {
    const updatedList = snippets.map(s => {
      if (s.id === item.id) {
        return { ...s, active: !s.active };
      }
      return s;
    });

    const statusMsg = !item.active 
      ? __('Snippet activated successfully!', 'huge-addons-for-elementor')
      : __('Snippet deactivated successfully!', 'huge-addons-for-elementor');

    saveToDb(updatedList, statusMsg);
  };

  const handleDelete = (item) => {
    if (!window.confirm(sprintf(__('Are you sure you want to delete "%s"?', 'huge-addons-for-elementor'), item.title))) {
      return;
    }

    const updatedList = snippets.filter(s => s.id !== item.id);
    saveToDb(updatedList, __('Snippet deleted successfully!', 'huge-addons-for-elementor'));
  };

  const getTypeLabel = (itemType) => {
    switch (itemType) {
      case 'php': return 'PHP';
      case 'css': return 'CSS';
      case 'javascript': return 'JavaScript';
      case 'html':
      default:
        return 'HTML';
    }
  };

  const getLocationLabel = (item) => {
    if (item.type === 'php') {
      switch (item.scope) {
        case 'frontend': return __('Frontend Only', 'huge-addons-for-elementor');
        case 'admin': return __('Admin Only', 'huge-addons-for-elementor');
        case 'everywhere':
        default:
          return __('Everywhere', 'huge-addons-for-elementor');
      }
    } else {
      switch (item.location) {
        case 'head': return __('Header (<head>)', 'huge-addons-for-elementor');
        case 'body_start': return __('Body Start (<body>)', 'huge-addons-for-elementor');
        case 'footer':
        default:
          return __('Footer (</body>)', 'huge-addons-for-elementor');
      }
    }
  };

  return (
    <div className="custom-code-tab-wrapper">
      <div className="custom-code-header-wrap">
        <h2>{__('Custom Code Snippets', 'huge-addons-for-elementor')}</h2>
        {!isEditing && (
          <button className="save-settings-btn" onClick={handleAddNew}>
            + {__('Add Custom Code', 'huge-addons-for-elementor')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="thha-card custom-code-form-card">
          <h3>{editItem ? __('Edit Custom Code', 'huge-addons-for-elementor') : __('Add New Custom Code', 'huge-addons-for-elementor')}</h3>
          <form onSubmit={handleSave}>
            <div className="setting-block-col">
              <label className="form-label">{__('Snippet Title', 'huge-addons-for-elementor')}</label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                className="version-select"
                placeholder={__('e.g. Chatbot Integration, Analytics Tracking', 'huge-addons-for-elementor')}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div className="setting-block-col">
              <label className="form-label">{__('Snippet Type', 'huge-addons-for-elementor')}</label>
              <select 
                value={type} 
                onChange={e => setType(e.target.value)} 
                className="action-select"
                style={{ width: '100%' }}
              >
                <option value="html">{__('HTML/JS Snippet (raw tag)', 'huge-addons-for-elementor')}</option>
                <option value="php">{__('PHP Code (evaluates server-side)', 'huge-addons-for-elementor')}</option>
                <option value="css">{__('CSS Styles (wrapped in style tag)', 'huge-addons-for-elementor')}</option>
                <option value="javascript">{__('JavaScript (wrapped in script tag)', 'huge-addons-for-elementor')}</option>
              </select>
            </div>

            {type === 'php' ? (
              <div className="setting-block-col">
                <label className="form-label">{__('Execution Scope', 'huge-addons-for-elementor')}</label>
                <select 
                  value={scope} 
                  onChange={e => setScope(e.target.value)} 
                  className="action-select"
                  style={{ width: '100%' }}
                >
                  <option value="everywhere">{__('Run Everywhere (Frontend & Admin)', 'huge-addons-for-elementor')}</option>
                  <option value="frontend">{__('Only on Frontend', 'huge-addons-for-elementor')}</option>
                  <option value="admin">{__('Only in Admin Area', 'huge-addons-for-elementor')}</option>
                </select>
              </div>
            ) : (
              <div className="setting-block-col">
                <label className="form-label">{__('Location / Injection Hook', 'huge-addons-for-elementor')}</label>
                <select 
                  value={location} 
                  onChange={e => setLocation(e.target.value)} 
                  className="action-select"
                  style={{ width: '100%' }}
                >
                  <option value="head">{__('Header (<head> section)', 'huge-addons-for-elementor')}</option>
                  <option value="body_start">{__('Body Start (immediately after <body> opening tag)', 'huge-addons-for-elementor')}</option>
                  <option value="footer">{__('Footer (immediately before </body> closing tag)', 'huge-addons-for-elementor')}</option>
                </select>
              </div>
            )}

            <div className="setting-block-col">
              <label className="form-label">{__('Custom Code', 'huge-addons-for-elementor')}</label>
              <textarea 
                value={code} 
                onChange={e => setCode(e.target.value)} 
                className="version-select code-editor-textarea"
                rows={10}
                placeholder={
                  type === 'php' 
                    ? __("// Add your custom PHP code here. Do not include <?php tag.\n// e.g. add_action('wp_footer', function() {\n//   echo 'Hello world';\n// });", 'huge-addons-for-elementor')
                    : __("<!-- Paste your code here. e.g. Google Analytics / Chatbot scripts -->", 'huge-addons-for-elementor')
                }
                style={{ width: '100%', fontFamily: 'monospace', boxSizing: 'border-box' }}
              />
            </div>

            <div className="setting-block with-toggle" style={{ border: 'none', padding: '15px 0' }}>
              <div className="text-content">
                <h3>{__('Activate Snippet', 'huge-addons-for-elementor')}</h3>
                <p>{__('Toggle off to keep the code saved but disabled.', 'huge-addons-for-elementor')}</p>
              </div>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={active} 
                  onChange={e => setActive(e.target.checked)} 
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="form-action-buttons">
              <button type="submit" className="save-settings-btn" disabled={isSaving}>
                {isSaving ? __('Saving...', 'huge-addons-for-elementor') : __('Save Snippet', 'huge-addons-for-elementor')}
              </button>
              <button type="button" className="outline-action-btn" onClick={handleCancel} disabled={isSaving}>
                {__('Cancel', 'huge-addons-for-elementor')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="custom-code-list-container">
          {snippets.length === 0 ? (
            <div className="placeholder-coming-soon-wrapper" style={{ marginTop: '0' }}>
              <div className="coming-soon-card">
                <div className="coming-soon-icon-wrap" style={{ background: '#E5EFFF', borderRadius: '50%', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0095FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                </div>
                <h2 className="coming-soon-title">{__('No Custom Codes Yet', 'huge-addons-for-elementor')}</h2>
                <p className="coming-soon-desc">
                  {__('Add custom HTML, CSS, JavaScript, or PHP code snippets to extend your site functionality, embed chatbots, or integrate tracking tools.', 'huge-addons-for-elementor')}
                </p>
                <button className="save-settings-btn" onClick={handleAddNew} style={{ float: 'none', margin: '15px auto 0' }}>
                  + {__('Add Custom Code', 'huge-addons-for-elementor')}
                </button>
              </div>
            </div>
          ) : (
            <div className="thha-card custom-code-table-card">
              <table className="custom-code-table">
                <thead>
                  <tr>
                    <th>{__('Name', 'huge-addons-for-elementor')}</th>
                    <th>{__('Type', 'huge-addons-for-elementor')}</th>
                    <th>{__('Target / Location', 'huge-addons-for-elementor')}</th>
                    <th>{__('Status', 'huge-addons-for-elementor')}</th>
                    <th style={{ textAlign: 'right' }}>{__('Actions', 'huge-addons-for-elementor')}</th>
                  </tr>
                </thead>
                <tbody>
                  {snippets.map((item) => (
                    <tr key={item.id}>
                      <td className="snippet-name-cell">
                        <strong>{item.title}</strong>
                      </td>
                      <td>
                        <span className={`type-badge badge-${item.type}`}>
                          {getTypeLabel(item.type)}
                        </span>
                      </td>
                      <td>
                        <span className="location-text">
                          {getLocationLabel(item)}
                        </span>
                      </td>
                      <td>
                        <label className="switch">
                          <input 
                            type="checkbox" 
                            checked={item.active} 
                            onChange={() => handleToggleActive(item)} 
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="row-action-btn edit" onClick={() => handleEdit(item)}>
                          {__('Edit', 'huge-addons-for-elementor')}
                        </button>
                        <button className="row-action-btn delete" onClick={() => handleDelete(item)}>
                          {__('Delete', 'huge-addons-for-elementor')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomCodeTab;
